import { type ILanguageType } from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import {
  type ICountryLanguageEntry,
  type ICountryLanguagesState,
} from './types';

export const preloadedState: ICountryLanguagesState = {
  inProcess: false,
  languages: [],
  allLanguages: [],
};

const countryLanguagesSlice = createSlice({
  name: 'CountryLanguages',
  initialState: preloadedState,
  reducers: {
    countryLanguagesLoad: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(isoCode2: string) {
        return { payload: { isoCode2 } };
      },
    },
    countryLanguagesLoadResponse: {
      reducer(
        state,
        action: { payload: { languages: ICountryLanguageEntry[] } },
      ) {
        state.inProcess = false;
        state.languages = action.payload.languages;
      },
      prepare(languages: ICountryLanguageEntry[]) {
        return { payload: { languages } };
      },
    },
    countryLanguagesLoadAllLanguages(state) {
      state.inProcess = true;
    },
    countryLanguagesLoadAllLanguagesResponse: {
      reducer(state, action: { payload: { allLanguages: ILanguageType[] } }) {
        state.inProcess = false;
        state.allLanguages = action.payload.allLanguages;
      },
      prepare(allLanguages: ILanguageType[]) {
        return { payload: { allLanguages } };
      },
    },
    countryLanguagesAdd: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(isoCode2: string, languageCode: string, isPrimary: boolean) {
        return { payload: { isoCode2, languageCode, isPrimary } };
      },
    },
    countryLanguagesAddResponse: {
      reducer(
        state,
        action: { payload: { languages: ICountryLanguageEntry[] } },
      ) {
        state.inProcess = false;
        state.languages = action.payload.languages;
      },
      prepare(languages: ICountryLanguageEntry[]) {
        return { payload: { languages } };
      },
    },
    countryLanguagesRemove: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(isoCode2: string, languageCode: string) {
        return { payload: { isoCode2, languageCode } };
      },
    },
    countryLanguagesRemoveResponse: {
      reducer(
        state,
        action: { payload: { languages: ICountryLanguageEntry[] } },
      ) {
        state.inProcess = false;
        state.languages = action.payload.languages;
      },
      prepare(languages: ICountryLanguageEntry[]) {
        return { payload: { languages } };
      },
    },
    countryLanguagesSetPrimary: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(isoCode2: string, languageCode: string) {
        return { payload: { isoCode2, languageCode } };
      },
    },
    countryLanguagesSetPrimaryResponse: {
      reducer(
        state,
        action: { payload: { languages: ICountryLanguageEntry[] } },
      ) {
        state.inProcess = false;
        state.languages = action.payload.languages;
      },
      prepare(languages: ICountryLanguageEntry[]) {
        return { payload: { languages } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('CountryLanguages'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  countryLanguagesLoad,
  countryLanguagesLoadResponse,
  countryLanguagesLoadAllLanguages,
  countryLanguagesLoadAllLanguagesResponse,
  countryLanguagesAdd,
  countryLanguagesAddResponse,
  countryLanguagesRemove,
  countryLanguagesRemoveResponse,
  countryLanguagesSetPrimary,
  countryLanguagesSetPrimaryResponse,
} = countryLanguagesSlice.actions;
export default countryLanguagesSlice.reducer;
